import {
  EuiFlexGroup,
  EuiFlexItem,
  EuiPanel,
  EuiStat,
  EuiText,
} from "@elastic/eui";
import { useAnketaI18n } from "src/internationalization";
import { WaveSummary } from "./model";

/**
 * The "is anything waiting on me?" row. Answers the daily triage question
 * without scrolling the chain: how many waves have something to promote, how
 * many are blocked, how many updates are open, how big the fleet is, and how far
 * behind the wave currently in focus is.
 */
export const WaveSummaryStats = ({
  summaries,
  selected,
}: {
  summaries: WaveSummary[];
  selected: WaveSummary | undefined;
}): JSX.Element => {
  const { translate } = useAnketaI18n();

  const withUpdates = summaries.filter((s) => s.state === "updates").length;
  const blocked = summaries.filter((s) => s.state === "blocked").length;
  const openUpdates = summaries.filter((s) => s.hasOpenMr).length;
  const appliances = summaries.reduce((n, s) => n + s.appliances.length, 0);
  const wavesWithAppliances = summaries.filter(
    (s) => s.appliances.length > 0,
  ).length;

  const tiles = [
    {
      title: withUpdates,
      color: withUpdates ? "warning" : "default",
      label: translate("anketa.opsanio.waves.stat.updates", {
        defaultMessage: "Waves with updates",
      }),
      note: translate("anketa.opsanio.waves.stat.updates.note", {
        defaultMessage: "ready to promote",
      }),
    },
    {
      title: blocked,
      color: blocked ? "danger" : "default",
      label: translate("anketa.opsanio.waves.stat.blocked", {
        defaultMessage: "Blocked",
      }),
      note: translate("anketa.opsanio.waves.stat.blocked.note", {
        defaultMessage: "parent not ready",
      }),
    },
    {
      title: openUpdates,
      color: openUpdates ? "primary" : "default",
      label: translate("anketa.opsanio.waves.stat.open", {
        defaultMessage: "Open updates",
      }),
      note: translate("anketa.opsanio.waves.stat.open.note", {
        defaultMessage: "awaiting merge",
      }),
    },
    {
      title: appliances,
      color: "default",
      label: translate("anketa.opsanio.waves.stat.appliances", {
        defaultMessage: "Appliances",
      }),
      note: translate("anketa.opsanio.waves.stat.appliances.note", {
        values: { count: wavesWithAppliances },
        defaultMessage: "across {{count}} waves",
      }),
    },
    {
      title:
        selected && selected.state !== "unconfig" ? selected.outdatedCount : "-",
      color: selected?.outdatedCount ? "warning" : "success",
      label: translate("anketa.opsanio.waves.stat.behind", {
        defaultMessage: "Components behind",
      }),
      note: translate("anketa.opsanio.waves.stat.behind.note", {
        values: { wave: selected?.wave ?? "-" },
        defaultMessage: "in {{wave}}",
      }),
    },
  ];

  return (
    <EuiFlexGroup gutterSize="m" responsive={false} wrap>
      {tiles.map((tile) => (
        <EuiFlexItem key={tile.label} style={{ minWidth: 180 }}>
          <EuiPanel hasBorder paddingSize="m" hasShadow={false}>
            <EuiStat
              title={tile.title}
              titleSize="m"
              titleColor={tile.color}
              description={tile.label}
              textAlign="left"
              reverse
            >
              <EuiText size="xs" color="subdued">
                {tile.note}
              </EuiText>
            </EuiStat>
          </EuiPanel>
        </EuiFlexItem>
      ))}
    </EuiFlexGroup>
  );
};
